import { PayloadAction } from "@reduxjs/toolkit"; // <-- Import PayloadAction
import { call, put, select, takeEvery } from "redux-saga/effects";
import { showAlert } from "src/components/UI/CustomAlert";
import { getRegisterFailureMessage } from "src/utils/registerErrorMessage";
import {
  authLogin,
  authRegister,
  createAddress,
  deleteAddress,
  getAddress,
  getCreditHistoryApi,
  getCreditPointsApi,
  sendOtpApi,
  updateUserProfileApi,
  verifyOtpApi,
} from "../apis/user";
import { selectUserId } from "../selectors/user";
import {
  deleteAddressAction,
  deleteAddressFailure,
  deleteAddressSuccess,
  getAddressAction,
  getAddressFailure,
  getAddressSuccess,
  getCreditPointsAction,
  getCreditPointsFailure,
  getCreditPointsSuccess,
  getCreditHistoryAction,
  getCreditHistoryFailure,
  getCreditHistorySuccess,
  getRegisterAction,
  getRegisterFailure,
  getRegisterSuccess,
  getUserAction,
  getUserFailure,
  getUserSuccess,
  postAddressAction,
  postAddressFailure,
  postAddressSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  sendOtp,
  sendOtpFailure,
  sendOtpSuccess,
  updateUserProfile,
  updateUserProfileFailure,
  updateUserProfileSuccess,
} from "../slices/user";

// Fetch user saga
function* getUser(action: PayloadAction<any>): Generator {
  try {
    const user: any = yield call(authLogin, action.payload);
    yield put(getUserSuccess(user));
  } catch (error: any) {
    yield put(getUserFailure(error.message || "Failed to fetch user"));
  }
}

// Register user saga
function* registerUser(action: PayloadAction<any>): Generator {
  try {
    const response: any = yield call(authRegister, action?.payload?.data);
    if ("User registered successfully" === response.message) {
      showAlert("success", "Please wait for approval");
      action?.payload?.navigate("/login");
    }
    yield put(getRegisterSuccess(response));
  } catch (error: unknown) {
    yield put(getRegisterFailure(getRegisterFailureMessage(error)));
  }
}

// Fetch address saga
function* fetchAddress(action: PayloadAction<any>): Generator {
  try {
    const address: any = yield call(getAddress, action.payload);
    yield put(getAddressSuccess(address));
  } catch (error: any) {
    yield put(getAddressFailure(error.message || "Failed to fetch address"));
  }
}

// Post address saga
function* postAddress(action: PayloadAction<any>): Generator {
  try {
    const addressResponse: any = yield call(
      createAddress,
      action.payload.id,
      action.payload.address,
    );
    yield put(postAddressSuccess(addressResponse));
  } catch (error: any) {
    yield put(postAddressFailure(error.message || "Failed to post address"));
  }
}

// Delete address saga
function* deleteAddressSaga(
  action: PayloadAction<{ customerId: any; addressId: any }>,
): Generator {
  try {
    const { customerId, addressId } = action.payload;
    // Call the deleteAddress function with customerId and addressId as arguments
    yield call(deleteAddress, customerId, addressId);
    yield put(deleteAddressSuccess(addressId)); // Dispatch success with the address ID
  } catch (error: any) {
    yield put(
      deleteAddressFailure(error.message || "Failed to delete address"),
    ); // Dispatch failure
  }
}

function* updateUserProfileSaga(
  action: PayloadAction<{ userId: string; profileData: any }>,
): Generator {
  try {
    // Assuming you have an API function to update user profile
    const updatedUser: any = yield call(
      updateUserProfileApi,
      action.payload.userId,
      action.payload.profileData,
    );
    yield put(updateUserProfileSuccess(updatedUser));
    showAlert("success", "Profile updated successfully");
  } catch (error: any) {
    showAlert("error", error.message || "Failed to update profile");
    yield put(
      updateUserProfileFailure(error.message || "Failed to update profile"),
    );
  }
}

function* sendOtpSaga(
  action: PayloadAction<{ email: string; navigate: (path: string) => void }>,
): Generator {
  try {
    const { email, navigate } = action.payload;
    const response: any = yield call(sendOtpApi, email);
    if (response) {
      showAlert("info", response?.message || "OTP sent successfully");
      yield put(sendOtpSuccess(response));
      navigate("/reset-password/" + email);
    } else {
      throw new Error(response?.message || "Failed to send OTP");
    }
  } catch (error: any) {
    yield put(sendOtpFailure(error.message || "Failed to send OTP"));
  }
}
function* resetPasswordSaga(
  action: PayloadAction<{
    email: string;
    newPassword: string;
    otp: string;
    navigate: (path: string) => void;
  }>,
): Generator {
  try {
    const { email, newPassword, otp, navigate } = action.payload;
    const payload = {
      email,
      newPassword,
      otp,
    };

    const response: any = yield call(verifyOtpApi, payload);

    if (response) {
      showAlert("success", response?.message || "Password reset successfully");
      yield put(resetPasswordSuccess(response));
      navigate("/login");
    } else {
      throw new Error(response?.message || "Failed to reset password");
    }
  } catch (error: any) {
    yield put(
      resetPasswordFailure(error?.message || "Failed to reset password"),
    );
  }
}

function* getCreditPointsSaga(action: PayloadAction<any>): Generator {
  try {
    const userId = yield select(selectUserId);
    const response: any = yield call(getCreditPointsApi, userId as string);
    yield put(getCreditPointsSuccess(response));
  } catch (error: any) {
    yield put(
      getCreditPointsFailure(error.message || "Failed to get credit points"),
    );
  }
}

function* getCreditHistorySaga(action: PayloadAction<any>): Generator {
  try {
    const response: any = yield call(getCreditHistoryApi, action.payload);
    yield put(getCreditHistorySuccess(response));
  } catch (error: any) {
    yield put(
      getCreditHistoryFailure(error.message || "Failed to get credit history"),
    );
  }
}

// Watcher Saga
export default function* userSaga() {
  yield takeEvery(getUserAction.type, getUser);
  yield takeEvery(getRegisterAction.type, registerUser);
  yield takeEvery(getAddressAction.type, fetchAddress);
  yield takeEvery(postAddressAction.type, postAddress);
  yield takeEvery(deleteAddressAction.type, deleteAddressSaga);
  yield takeEvery(updateUserProfile.type, updateUserProfileSaga);
  yield takeEvery(sendOtp.type, sendOtpSaga);
  yield takeEvery(resetPassword.type, resetPasswordSaga);
  yield takeEvery(getCreditPointsAction.type, getCreditPointsSaga);
  yield takeEvery(getCreditHistoryAction.type, getCreditHistorySaga);
}
